import { useState, useEffect, memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { getErrorMessage } from "@/lib/errorHandler";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys, invalidateQueries } from '@/lib/queryClient';
import { QuizGridSkeleton } from '@/components/QuizCardSkeleton';
import { SidebarSkeleton } from '@/components/FolderSkeleton';
import AssignedUsersPicker from '@/components/AssignedUsersPicker';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus, FileText, MoreVertical, Pencil, Trash2, Globe, Eye, Copy, XCircle, CopyPlus, Users, RotateCcw, Folder, FolderOpen, FolderPlus, MapPin, Building2, Check, X, AlertTriangle
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { formatQuizStatus, getQuizStatusColor } from "@/utils/statusFormatter";

// ✅ Memoized QuizCard Component for Performance
const QuizCard = memo(({
  quiz,
  campusName,
  canManageFolders,
  isSelected,
  onToggleSelect,
  onEdit,
  onMoveToFolder,
  onDuplicate,
  onCopyToCampus,
  showCopyToCampus,
  onCopyToOrg,
  showCopyToOrg,
  onAssignGroup,
  onPublish,
  onCopyLink,
  onPreview,
  onClose,
  onReopen,
  onDelete
}) => {
  return (
    <div className={`refined-quiz-card ${isSelected ? 'ring-2 ring-indigo-400 border-indigo-300' : ''}`} data-testid={`quiz-item-${quiz.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {onToggleSelect && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSelect(quiz.id); }}
              className={`flex-shrink-0 w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 hover:border-indigo-400 bg-white'
              }`}
            >
              {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-900 truncate mb-0.5">{quiz.title}</h3>
            <p className="text-xs text-slate-500 truncate">{quiz.description || "No description"}</p>
          </div>
        </div>
        {(onEdit || onDuplicate || onDelete || onAssignGroup || showCopyToCampus || showCopyToOrg ||
          (onMoveToFolder && canManageFolders) ||
          (onPublish && quiz.status === "draft") ||
          quiz.status === "published" ||
          (onReopen && quiz.status === "closed")) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded hover:bg-slate-100 transition-colors" data-testid={`quiz-menu-${quiz.id}`}>
              <MoreVertical className="w-4 h-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(quiz.id)} data-testid={`edit-quiz-${quiz.id}`}>
                <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
              </DropdownMenuItem>
            )}
            {onMoveToFolder && canManageFolders && (
              <DropdownMenuItem onClick={() => onMoveToFolder(quiz)}>
                <Folder className="w-3.5 h-3.5 mr-2" /> Move to Folder
              </DropdownMenuItem>
            )}
            {onDuplicate && (
              <DropdownMenuItem onClick={() => onDuplicate(quiz.id)} data-testid={`duplicate-quiz-${quiz.id}`}>
                <CopyPlus className="w-3.5 h-3.5 mr-2" /> Duplicate
              </DropdownMenuItem>
            )}
            {showCopyToCampus && (
              <DropdownMenuItem onClick={() => onCopyToCampus(quiz)} data-testid={`copy-campus-${quiz.id}`}>
                <Copy className="w-3.5 h-3.5 mr-2" /> Copy to Campus
              </DropdownMenuItem>
            )}
            {showCopyToOrg && (
              <DropdownMenuItem onClick={() => onCopyToOrg(quiz)} data-testid={`copy-org-${quiz.id}`}>
                <Building2 className="w-3.5 h-3.5 mr-2" /> Copy to Organization
              </DropdownMenuItem>
            )}
            {onAssignGroup && (
              <DropdownMenuItem onClick={() => onAssignGroup(quiz.id)} data-testid={`assign-group-${quiz.id}`}>
                <Users className="w-3.5 h-3.5 mr-2" /> Manage access
              </DropdownMenuItem>
            )}
            {onPublish && quiz.status === "draft" && (
              <DropdownMenuItem onClick={() => onPublish(quiz.id)} data-testid={`publish-quiz-${quiz.id}`}>
                <Globe className="w-3.5 h-3.5 mr-2" /> Publish
              </DropdownMenuItem>
            )}
            {quiz.status === "published" && (
              <>
                <DropdownMenuItem onClick={() => onCopyLink(quiz.slug)} data-testid={`copy-link-${quiz.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-2" /> Copy Link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPreview(quiz.slug)} data-testid={`preview-quiz-${quiz.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-2" /> Preview
                </DropdownMenuItem>
                {onClose && (
                  <DropdownMenuItem onClick={() => onClose(quiz.id)} data-testid={`close-quiz-${quiz.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-2" /> Close
                  </DropdownMenuItem>
                )}
              </>
            )}
            {onReopen && quiz.status === "closed" && (
              <DropdownMenuItem onClick={() => onReopen(quiz.id)} data-testid={`reopen-quiz-${quiz.id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-2" /> Re-open
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem onClick={() => onDelete(quiz.id)} className="text-red-600" data-testid={`delete-quiz-${quiz.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3 font-medium">
        <span>{quiz.question_count || 0} questions</span>
        <span className="w-1 h-1 rounded-full bg-slate-300" />
        <span>{quiz.submission_count || 0} submissions</span>
      </div>
      <div className="flex items-center justify-between">
        <Badge className={`border text-xs font-semibold ${getQuizStatusColor(quiz.status)}`}>{formatQuizStatus(quiz.status)}</Badge>
        <div className="flex items-center gap-2">
          {campusName && (
            <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
              <MapPin className="w-3 h-3" />{campusName}
            </span>
          )}
          <span className="text-xs text-slate-400 font-mono">/{quiz.slug}</span>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if quiz data actually changed
  return (
    prevProps.quiz.id === nextProps.quiz.id &&
    prevProps.quiz.updated_at === nextProps.quiz.updated_at &&
    prevProps.quiz.status === nextProps.quiz.status &&
    prevProps.canManageFolders === nextProps.canManageFolders &&
    prevProps.isSelected === nextProps.isSelected &&
    !!prevProps.onToggleSelect === !!nextProps.onToggleSelect &&
    !!prevProps.onEdit === !!nextProps.onEdit &&
    !!prevProps.onDuplicate === !!nextProps.onDuplicate &&
    !!prevProps.onDelete === !!nextProps.onDelete &&
    prevProps.showCopyToCampus === nextProps.showCopyToCampus &&
    prevProps.showCopyToOrg === nextProps.showCopyToOrg
  );
});

QuizCard.displayName = 'QuizCard';

export default function QuizList() {
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = all, "uncategorized" = no folder
  const [page, setPage] = useState(1); // Pagination state
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 50 });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { selectedCampus, isOrgAdmin, isMultiCampusAdmin, campuses } = useCampusContext();

  // All authenticated users can manage folders (basic organizational feature)
  const canManageFolders = true;
  const canManageGroups = isSuperadmin || user?.permissions?.can_manage_groups === true;
  const canCreateQuiz = isSuperadmin || user?.permissions?.can_create_quizzes === true;
  const canEditQuiz   = isSuperadmin || user?.permissions?.can_edit_quizzes === true;
  const canDeleteQuiz = isSuperadmin || user?.permissions?.can_delete_quizzes === true;
  const canWriteAny   = canCreateQuiz || canEditQuiz || canDeleteQuiz;

  // Folder management dialogs
  const [folderDialog, setFolderDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });

  // Delete folder dialog
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [deleteMode, setDeleteMode] = useState("move"); // "move" or "cascade"

  // Group assignment dialog
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignQuizId, setAssignQuizId] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [assignedGroups, setAssignedGroups] = useState([]);

  // Move quiz dialog
  const [moveQuizDialog, setMoveQuizDialog] = useState(false);
  const [movingQuiz, setMovingQuiz] = useState(null); // Store entire quiz object
  const [targetFolderId, setTargetFolderId] = useState("");

  // Copy to campus dialog
  const [copyToCampusQuiz, setCopyToCampusQuiz] = useState(null);
  const [copyToCampusOpen, setCopyToCampusOpen] = useState(false);
  const [copyTargetCampusId, setCopyTargetCampusId] = useState("");
  const [copyingToCampus, setCopyingToCampus] = useState(false);

  // Copy to Organization State (Superadmin only)
  const [copyToOrgQuiz, setCopyToOrgQuiz] = useState(null);
  const [copyToOrgOpen, setCopyToOrgOpen] = useState(false);
  const [copyTargetOrgId, setCopyTargetOrgId] = useState("");
  const [copyTargetOrgCampusId, setCopyTargetOrgCampusId] = useState("");
  const [copyNewTitle, setCopyNewTitle] = useState("");
  const [copyingToOrg, setCopyingToOrg] = useState(false);
  const [targetOrgCampuses, setTargetOrgCampuses] = useState([]);

  const queryClient = useQueryClient();

  // Bulk selection — state & callbacks that don't need `quizzes` go here
  const [selectedQuizIds, setSelectedQuizIds] = useState(new Set());

  const toggleSelectQuiz = useCallback((id) => {
    setSelectedQuizIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedQuizIds(new Set()), []);

  const handleBulkDelete = useCallback(async () => {
    if (!window.confirm(`Delete ${selectedQuizIds.size} quiz(zes)? This cannot be undone.`)) return;
    const ids = [...selectedQuizIds];
    let failed = 0;
    for (const id of ids) {
      try { await API.delete(`/quizzes/${id}`); } catch { failed++; }
    }
    toast.success(failed ? `Deleted ${ids.length - failed}, ${failed} failed` : `Deleted ${ids.length} quiz(zes)`);
    clearSelection();
    await Promise.all([
      queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId, page)),
      queryClient.refetchQueries(queryKeys.quizzes.list(null, undefined)),
      queryClient.refetchQueries(queryKeys.folders.all),
    ]);
  }, [selectedQuizIds, queryClient, selectedFolderId, page, clearSelection]);

  // Fetch organizations for superadmin (for copy to org feature)
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations', 'simple'],
    queryFn: async () => {
      const res = await API.get('/superadmin/organizations/simple');
      return res.data || [];
    },
    enabled: isSuperadmin, // Only fetch for superadmin
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Reset to page 1 when folder changes
  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [selectedFolderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch ALL quizzes (for counts in sidebar)
  const { data: allQuizzes = [] } = useQuery({
    queryKey: queryKeys.quizzes.list(null, undefined, selectedOrg?.id, selectedCampus?.id), // Include org and campus in cache key
    queryFn: async () => {
      const params = {};
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/quizzes", { params });
      // Handle both old and new response formats
      return res.data.quizzes || res.data;
    },
    staleTime: 0, // Always fetch fresh data for accurate counts
    refetchOnMount: true,
    keepPreviousData: false, // Don't keep old data when org changes
    enabled: !isSuperadmin || !!selectedOrg, // Only fetch if org is selected for superadmin
  });

  // Calculate uncategorized count
  const uncategorizedCount = allQuizzes.filter(q => !q.folder_id).length;

  // Fetch quizzes with React Query (filtered by folder, with pagination)
  const { data: quizzesData, isLoading: loadingQuizzes } = useQuery({
    queryKey: queryKeys.quizzes.list(selectedFolderId, page, selectedOrg?.id, selectedCampus?.id),
    queryFn: async () => {
      const params = {
        ...(selectedFolderId ? { folder_id: selectedFolderId } : {}),
        page,
        limit: 50
      };
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/quizzes", { params });

      // Handle new paginated response format
      if (res.data.quizzes && res.data.pagination) {
        setPagination(res.data.pagination);
        return res.data.quizzes;
      }
      // Fallback for backward compatibility
      return res.data;
    },
    staleTime: 0, // Always fetch fresh data
    keepPreviousData: false, // Don't keep old data when org changes
    enabled: !isSuperadmin || !!selectedOrg,
  });

  const quizzes = quizzesData || [];

  // Bulk callbacks that need `quizzes` (must come after quizzes is declared)
  const selectAllVisible = useCallback(() => {
    setSelectedQuizIds(new Set(quizzes.map(q => q.id)));
  }, [quizzes]);

  const handleBulkPublish = useCallback(async () => {
    const draftIds = quizzes.filter(q => selectedQuizIds.has(q.id) && q.status === 'draft').map(q => q.id);
    if (!draftIds.length) { toast.info('No draft quizzes in selection'); return; }
    let failed = 0;
    for (const id of draftIds) {
      try { await API.post(`/quizzes/${id}/publish`); } catch { failed++; }
    }
    toast.success(failed ? `Published ${draftIds.length - failed}, ${failed} failed` : `Published ${draftIds.length} quiz(zes)`);
    clearSelection();
    queryClient.invalidateQueries(queryKeys.quizzes.list(selectedFolderId, page));
  }, [selectedQuizIds, quizzes, queryClient, selectedFolderId, page, clearSelection]);

  // Fetch folders with React Query (get all for sidebar - use high limit)
  const { data: foldersData = {}, isLoading: loadingFolders } = useQuery({
    queryKey: queryKeys.folders.all(selectedOrg?.id, selectedCampus?.id),
    queryFn: async () => {
      const params = { limit: 100 };
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/quiz-folders", { params });
      return res.data;
    },
    staleTime: 0, // Always fetch fresh data
    keepPreviousData: false, // Don't keep old data when org changes
    enabled: canManageFolders && (!isSuperadmin || !!selectedOrg), // Only fetch if org is selected for superadmin
  });

  const folders = foldersData.folders || [];

  // Fetch groups (get all for dropdown - use high limit)
  const { data: groupsData = {} } = useQuery({
    queryKey: queryKeys.groups.all(selectedOrg?.id, selectedCampus?.id),
    queryFn: async () => {
      const params = { limit: 100 };
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/groups", { params });
      return res.data;
    },
    staleTime: 0, // Always fetch fresh data
    keepPreviousData: false, // Don't keep old data when org changes
    enabled: !isSuperadmin || !!selectedOrg, // Only fetch if org is selected for superadmin
  });

  const groups = groupsData.groups || [];

  const loading = loadingQuizzes || (canManageFolders && loadingFolders);

  // Folder Management Functions
  const openCreateFolder = () => {
    setEditingFolder(null);
    setFolderForm({ name: "", description: "" });
    setFolderDialog(true);
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderForm({ name: folder.name, description: folder.description });
    setFolderDialog(true);
  };

  // Folder mutations with optimistic updates
  const createFolderMutation = useMutation({
    mutationFn: async (data) => {
      const res = await API.post("/quiz-folders", data);
      return res.data;
    },
    onMutate: async (newFolder) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.folders.all });

      // Snapshot previous value
      const previousFolders = queryClient.getQueryData(queryKeys.folders.all);

      // Optimistically update - handle both object and array formats
      const tempId = `temp-${Date.now()}`;
      queryClient.setQueryData(queryKeys.folders.all, (old = {}) => {
        const currentFolders = old.folders || old || [];
        return {
          folders: [...currentFolders, { id: tempId, ...newFolder, quiz_count: 0 }],
          pagination: old.pagination || { total: currentFolders.length + 1, pages: 1, limit: 100 }
        };
      });

      return { previousFolders, tempId };
    },
    onError: (err, newFolder, context) => {
      // Rollback on error
      queryClient.setQueryData(queryKeys.folders.all, context.previousFolders);
      toast.error(getErrorMessage(err, "Failed to create folder"));
    },
    onSuccess: (data, variables, context) => {
      // Replace temp with real data - handle both object and array formats
      queryClient.setQueryData(queryKeys.folders.all, (old = {}) => {
        const currentFolders = old.folders || old || [];
        return {
          folders: currentFolders.map(f => f.id === context.tempId ? data : f),
          pagination: old.pagination
        };
      });
      toast.success("Folder created");
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await API.put(`/quiz-folders/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      invalidateQueries.folders(queryClient);
      toast.success("Folder updated");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to update folder"));
    },
  });

  const handleSaveFolder = async () => {
    if (!folderForm.name.trim()) {
      toast.error("Folder name is required");
      return;
    }

    try {
      if (editingFolder) {
        await updateFolderMutation.mutateAsync({
          id: editingFolder.id,
          data: folderForm
        });
      } else {
        const folderPayload = { ...folderForm };
        if (isSuperadmin && selectedOrg?.id) folderPayload.organization_id = selectedOrg.id;
        await createFolderMutation.mutateAsync(folderPayload);
      }
      setFolderDialog(false);
    } catch (err) {
      // Error is already handled by mutation's onError
      // Just log it for debugging
      console.error("Folder save error:", err);
    }
  };

  const openDeleteFolder = (folder) => {
    setDeletingFolder(folder);
    setDeleteMode("move");
    setDeleteFolderDialog(true);
  };

  const handleDeleteFolder = async () => {
    try {
      await API.delete(`/quiz-folders/${deletingFolder.id}`, {
        params: { delete_mode: deleteMode }
      });
      toast.success("Folder deleted");
      setDeleteFolderDialog(false);
      if (selectedFolderId === deletingFolder.id) {
        setSelectedFolderId(null);
      }

      // Force refetch to update counts immediately
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null)),
        queryClient.refetchQueries(queryKeys.folders.all)
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete folder"));
    }
  };

  // Move Quiz to Folder
  const openMoveQuiz = (quiz) => {
    setMovingQuiz(quiz); // Store entire quiz object
    setTargetFolderId("");
    setMoveQuizDialog(true);
  };

  const handleMoveQuiz = async () => {
    if (!targetFolderId && targetFolderId !== "uncategorized") {
      toast.error("Select a folder");
      return;
    }

    try {
      await API.put(`/quizzes/${movingQuiz.id}`, {
        folder_id: targetFolderId === "uncategorized" ? null : targetFolderId
      });

      toast.success("Quiz moved successfully");
      setMoveQuizDialog(false);
      setMovingQuiz(null);
      setTargetFolderId("");

      // Force refetch all queries to update counts immediately
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null)),
        queryClient.refetchQueries(queryKeys.folders.all)
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to move quiz"));
    }
  };

  // ✅ Memoized Quiz Management Functions (useCallback for performance)
  const handleEdit = useCallback((quizId) => {
    navigate(`/quizzes/${quizId}/edit`);
  }, [navigate]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this quiz? This cannot be undone.")) return;
    try {
      await API.delete(`/quizzes/${id}`);
      toast.success("Quiz deleted");
      // Force refetch to update counts immediately
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId, page)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null, undefined)),
        queryClient.refetchQueries(queryKeys.folders.all)
      ]);
    } catch (err) {
      toast.error("Failed to delete");
    }
  }, [queryClient, selectedFolderId, page]);

  const handlePublish = useCallback(async (id) => {
    try {
      await API.post(`/quizzes/${id}/publish`);
      toast.success("Quiz published!");
      queryClient.invalidateQueries(queryKeys.quizzes.list(selectedFolderId, page));
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to publish"));
    }
  }, [queryClient, selectedFolderId, page]);

  const handleClose = useCallback(async (id) => {
    try {
      await API.post(`/quizzes/${id}/close`);
      toast.success("Quiz closed");
      queryClient.invalidateQueries(queryKeys.quizzes.list(selectedFolderId, page));
    } catch (err) {
      toast.error("Failed to close quiz");
    }
  }, [queryClient, selectedFolderId, page]);

  const handleDuplicate = useCallback(async (id) => {
    try {
      const res = await API.post(`/quizzes/${id}/duplicate`);
      toast.success(`Quiz duplicated as "${res.data.title}"`);
      // Force refetch to update counts immediately
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId, page)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null, undefined)),
        queryClient.refetchQueries(queryKeys.folders.all)
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to duplicate"));
    }
  }, [queryClient, selectedFolderId, page]);

  const handleReopen = useCallback(async (id) => {
    try {
      await API.post(`/quizzes/${id}/reopen`);
      toast.success("Quiz re-opened and published");
      queryClient.invalidateQueries(queryKeys.quizzes.list(selectedFolderId, page));
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to re-open quiz"));
    }
  }, [queryClient, selectedFolderId, page]);

  const handleCopyToCampus = useCallback((quiz) => {
    setCopyToCampusQuiz(quiz);
    setCopyTargetCampusId("");
    setCopyToCampusOpen(true);
  }, []);

  const confirmCopyToCampus = async () => {
    if (!copyTargetCampusId || !copyToCampusQuiz) return;
    setCopyingToCampus(true);
    try {
      await API.post(`/quizzes/${copyToCampusQuiz.id}/duplicate?target_campus_id=${copyTargetCampusId}`);
      const targetName = campuses.find(c => c.id === copyTargetCampusId)?.name;
      toast.success(`Quiz copied to ${targetName} as draft`);
      setCopyToCampusOpen(false);
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId, page)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null, undefined)),
        queryClient.refetchQueries(queryKeys.folders.all)
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to copy quiz"));
    } finally {
      setCopyingToCampus(false);
    }
  };

  // Copy to Organization handlers (Superadmin only)
  const handleCopyToOrg = useCallback((quiz) => {
    setCopyToOrgQuiz(quiz);
    setCopyTargetOrgId("");
    setCopyTargetOrgCampusId("");
    setCopyNewTitle("");
    setTargetOrgCampuses([]);
    setCopyToOrgOpen(true);
  }, []);

  // Fetch campuses when target org is selected
  const fetchTargetOrgCampuses = async (orgId) => {
    if (!orgId) {
      setTargetOrgCampuses([]);
      return;
    }
    try {
      const res = await API.get('/campuses', { params: { organization_id: orgId } });
      setTargetOrgCampuses(res.data.campuses || []);
    } catch (err) {
      console.error('Failed to fetch campuses:', err);
      setTargetOrgCampuses([]);
    }
  };

  const confirmCopyToOrg = async () => {
    if (!copyTargetOrgId || !copyToOrgQuiz) {
      toast.error("Please select a target organization");
      return;
    }

    setCopyingToOrg(true);
    try {
      const params = {
        target_organization_id: copyTargetOrgId
      };

      if (copyTargetOrgCampusId) {
        params.target_campus_id = copyTargetOrgCampusId;
      }

      if (copyNewTitle && copyNewTitle.trim()) {
        params.new_title = copyNewTitle.trim();
      }

      const res = await API.post(`/quizzes/${copyToOrgQuiz.id}/copy-to-organization`, null, { params });

      const targetOrgName = res.data.target_organization_name || 'target organization';
      toast.success(`Quiz successfully copied to ${targetOrgName}`, {
        description: `${res.data.copied_items.questions} questions and ${res.data.copied_items.sections} sections copied. Status: draft`
      });

      setCopyToOrgOpen(false);

      // Refetch quiz list
      await Promise.all([
        queryClient.refetchQueries(queryKeys.quizzes.list(selectedFolderId, page)),
        queryClient.refetchQueries(queryKeys.quizzes.list(null, undefined))
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to copy quiz to organization"));
    } finally {
      setCopyingToOrg(false);
    }
  };

  const openAssignGroup = useCallback(async (quizId) => {
    setAssignQuizId(quizId);
    setSelectedGroupId("");
    // Fetch already assigned groups for this quiz
    try {
      const res = await API.get(`/quizzes/${quizId}/assigned-groups`);
      setAssignedGroups(res.data || []);
    } catch (err) {
      console.error("Failed to fetch assigned groups:", err);
      setAssignedGroups([]);
    }
    setAssignDialog(true);
  }, []);

  const handleAssignGroup = useCallback(async () => {
    if (!selectedGroupId) { toast.error("Select a group"); return; }
    try {
      await API.post(`/quizzes/${assignQuizId}/assign-group`, { group_id: selectedGroupId });
      toast.success("Group assigned to quiz");
      // Refresh assigned groups list
      const res = await API.get(`/quizzes/${assignQuizId}/assigned-groups`);
      setAssignedGroups(res.data || []);
      setSelectedGroupId("");
      // Invalidate quizzes to refresh the data
      queryClient.invalidateQueries({ queryKey: queryKeys.quizzes.all });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to assign group"));
    }
  }, [selectedGroupId, assignQuizId, queryClient]);

  const handleUnassignGroup = useCallback(async (groupId) => {
    try {
      await API.delete(`/quizzes/${assignQuizId}/unassign-group/${groupId}`);
      toast.success("Group unassigned from quiz");
      // Refresh assigned groups list
      const res = await API.get(`/quizzes/${assignQuizId}/assigned-groups`);
      setAssignedGroups(res.data || []);
      // Invalidate quizzes to refresh the data
      queryClient.invalidateQueries({ queryKey: queryKeys.quizzes.all });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to unassign group"));
    }
  }, [assignQuizId, queryClient]);

  const copyLink = useCallback((slug) => {
    const url = `${window.location.origin}/quiz/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Quiz link copied!");
  }, []);

  const handlePreview = useCallback((slug) => {
    window.open(`/quiz/${slug}`, '_blank');
  }, []);

  // Show skeletons while loading
  if (loading) {
    return (
      <>
        <style>{`
        `}</style>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Quizzes</h1>
              <p className="text-sm text-slate-500 font-light mt-1">Manage your quizzes and assessments</p>
            </div>
          </div>
          <div className="flex gap-6">
            {canManageFolders && <SidebarSkeleton />}
            <div className="flex-1">
              <QuizGridSkeleton count={6} />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`

        .refined-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .refined-header {
          font-family: 'Lexend', sans-serif;
          font-weight: 600;
          letter-spacing: -0.025em;
        }

        .refined-quiz-card {
          background: white;
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding: 20px;
          transition: all 0.2s ease;
        }

        .refined-quiz-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px 0 rgb(0 0 0 / 0.06);
          border-color: rgb(203 213 225);
        }

        .refined-sidebar {
          background: linear-gradient(to bottom, rgb(248 250 252), white);
          border: 1px solid rgb(226 232 240);
          border-radius: 12px;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.02);
        }

        .refined-folder-button {
          transition: all 0.2s ease;
        }

        .refined-folder-button:hover:not(.refined-folder-active) {
          background: white;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
        }

        .refined-folder-active {
          background: linear-gradient(135deg, rgb(99 102 241), rgb(79 70 229));
          color: white;
          box-shadow: 0 4px 8px 0 rgb(99 102 241 / 0.3);
        }

        .refined-action-button {
          transition: all 0.2s ease;
        }

        .refined-action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 0.1);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in {
          animation: fadeIn 0.4s ease;
        }
      `}</style>

      <div className="refined-root space-y-6" data-testid="quiz-list-page">
        {/* Header */}
        <div className="flex items-center justify-between fade-in">
          <div>
            <h1 className="refined-header text-3xl text-slate-900 flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                <FileText className="h-6 w-6 text-indigo-700" />
              </div>
              Quizzes
            </h1>
            <p className="text-sm text-slate-600 mt-2 ml-14">
              Manage your quizzes and assessments
            </p>
          </div>
          {canCreateQuiz && (
            <Link to="/quizzes/new">
              <Button data-testid="new-quiz-btn" className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> New Quiz
              </Button>
            </Link>
          )}
        </div>

        {/* Main Content - Sidebar + Quiz Grid */}
        <div className="flex gap-6 h-[calc(100vh-12rem)]">
          {/* SIDEBAR - Only show if user can manage folders */}
          {canManageFolders && (
            <div className="w-72 flex-shrink-0 overflow-auto refined-sidebar p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="refined-header text-sm text-slate-900 flex items-center gap-2">
                  <Folder className="w-4 h-4 text-slate-600" />
                  Folders
                </h3>
                <Button
                  size="sm"
                  onClick={openCreateFolder}
                  className="refined-action-button h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <FolderPlus className="w-3.5 h-3.5 mr-1.5" /> New
                </Button>
              </div>

              <div className="space-y-1.5">
                {/* All Quizzes */}
                <button
                  onClick={() => setSelectedFolderId(null)}
                  className={`refined-folder-button w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    selectedFolderId === null ? "refined-folder-active" : "text-slate-700"
                  }`}
                >
                  <FolderOpen className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left font-semibold">All Quizzes</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    selectedFolderId === null
                      ? "bg-indigo-500/30 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}>
                    {allQuizzes.length}
                  </span>
                </button>

                {/* Uncategorized */}
                <button
                  onClick={() => setSelectedFolderId("uncategorized")}
                  className={`refined-folder-button w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    selectedFolderId === "uncategorized" ? "refined-folder-active" : "text-slate-700"
                  }`}
                >
                  <Folder className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left font-semibold">Uncategorized</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    selectedFolderId === "uncategorized"
                      ? "bg-indigo-500/30 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}>
                    {uncategorizedCount}
                  </span>
                </button>

                <div className="h-px bg-slate-300 my-4" />

                {/* Custom Folders */}
                <div className="space-y-1.5">
                  {folders.length === 0 ? (
                    <div className="text-center py-8 px-3">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-3 opacity-70">
                        <rect x="4" y="14" width="40" height="28" rx="4" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                        <path d="M4 18C4 15.8 5.8 14 8 14H20L24 18H40C42.2 18 44 19.8 44 22V38C44 40.2 42.2 42 40 42H8C5.8 42 4 40.2 4 38V18Z" fill="#E0E7FF" stroke="#A5B4FC" strokeWidth="1.5"/>
                        <circle cx="36" cy="36" r="8" fill="#4F46E5"/>
                        <path d="M33 36 L35.5 38.5 L39 34" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p className="text-xs text-slate-600 font-medium">No folders yet</p>
                      <p className="text-xs text-slate-400 mt-0.5">Click "+ New" to organize your quizzes</p>
                    </div>
                  ) : (
                    folders.map((folder) => (
                      <div key={folder.id} className="group relative">
                        <div className={`refined-folder-button flex items-center gap-2 rounded-lg ${
                          selectedFolderId === folder.id ? "refined-folder-active" : "text-slate-700"
                        }`}>
                          <button
                            onClick={() => setSelectedFolderId(folder.id)}
                            className="flex-1 flex items-center gap-3 px-3 py-2.5 text-sm"
                          >
                            <Folder className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 text-left font-semibold truncate">{folder.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              selectedFolderId === folder.id
                                ? "bg-indigo-500/30 text-white"
                                : "bg-slate-200 text-slate-600"
                            }`}>
                              {folder.quiz_count || 0}
                            </span>
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className={`p-2 mr-1 rounded-md transition-all ${
                                  selectedFolderId === folder.id
                                    ? "opacity-100 hover:bg-indigo-600"
                                    : "opacity-0 group-hover:opacity-100 hover:bg-slate-200"
                                }`}
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => openEditFolder(folder)}>
                                <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openDeleteFolder(folder)} className="text-red-600">
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MAIN CONTENT */}
          <div className="flex-1 overflow-auto">
            {quizzes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <svg width="100" height="90" viewBox="0 0 100 90" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-5 opacity-80">
                  <rect x="10" y="8" width="68" height="74" rx="6" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                  <rect x="22" y="22" width="44" height="5" rx="2.5" fill="#C7D2FE"/>
                  <rect x="22" y="33" width="32" height="4" rx="2" fill="#DDE3F8"/>
                  <rect x="22" y="43" width="38" height="4" rx="2" fill="#DDE3F8"/>
                  <rect x="22" y="53" width="28" height="4" rx="2" fill="#DDE3F8"/>
                  <circle cx="76" cy="68" r="16" fill="#4F46E5"/>
                  <path d="M70 68 L74.5 72.5 L82 63" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="text-slate-700 font-semibold mb-1">
                  {selectedFolderId === "uncategorized" ? "No uncategorized quizzes" : selectedFolderId ? "This folder is empty" : "No quizzes yet"}
                </p>
                <p className="text-sm text-slate-500 mb-5">
                  {selectedFolderId ? "Move quizzes here using the quiz menu" : "Create your first assessment to get started"}
                </p>
                {!selectedFolderId && (
                  <Link to="/quizzes/new">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm">
                      <Plus className="w-4 h-4 mr-2" /> Create Quiz
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {quizzes.map((quiz) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    campusName={campuses.length > 1 ? campuses.find(c => c.id === quiz.campus_id)?.name : null}
                    canManageFolders={canManageFolders}
                    isSelected={selectedQuizIds.has(quiz.id)}
                    onToggleSelect={canWriteAny ? toggleSelectQuiz : null}
                    onEdit={canEditQuiz ? handleEdit : null}
                    onMoveToFolder={canEditQuiz ? openMoveQuiz : null}
                    onDuplicate={canCreateQuiz ? handleDuplicate : null}
                    onCopyToCampus={handleCopyToCampus}
                    showCopyToCampus={canCreateQuiz && isOrgAdmin && campuses.length > 1}
                    onCopyToOrg={handleCopyToOrg}
                    showCopyToOrg={canCreateQuiz && isSuperadmin}
                    onAssignGroup={canManageGroups ? openAssignGroup : null}
                    onPublish={canEditQuiz ? handlePublish : null}
                    onCopyLink={copyLink}
                    onPreview={handlePreview}
                    onClose={canEditQuiz ? handleClose : null}
                    onReopen={canEditQuiz ? handleReopen : null}
                    onDelete={canDeleteQuiz ? handleDelete : null}
                  />
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {quizzes.length > 0 && pagination.pages > 1 && (
              <div className="flex justify-between items-center mt-6 px-5 py-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600 font-medium">
                  Showing {((page - 1) * pagination.limit) + 1}-{Math.min(page * pagination.limit, pagination.total)} of {pagination.total} quizzes
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="refined-action-button h-9 border-slate-300"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1 px-3 text-sm text-slate-700">
                    Page <span className="font-bold">{page}</span> of <span className="font-bold">{pagination.pages}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.pages}
                    onClick={() => setPage(p => p + 1)}
                    className="refined-action-button h-9 border-slate-300"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Folder Dialog */}
        <Dialog open={folderDialog} onOpenChange={setFolderDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="refined-header text-slate-900">
                {editingFolder ? "Edit Folder" : "Create Folder"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Folder Name</Label>
                <Input
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                  placeholder="Enter folder name"
                  className="h-10 border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Description (Optional)</Label>
                <Textarea
                  value={folderForm.description}
                  onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                  placeholder="Enter folder description"
                  rows={3}
                  className="border-slate-300"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFolderDialog(false)} className="border-slate-300">Cancel</Button>
              <Button onClick={handleSaveFolder} className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white">
                {editingFolder ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Folder Dialog */}
        <AlertDialog open={deleteFolderDialog} onOpenChange={setDeleteFolderDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="refined-header text-slate-900">Delete Folder: {deletingFolder?.name}</AlertDialogTitle>
              <AlertDialogDescription className="pt-2">
                This folder contains <strong>{deletingFolder?.quiz_count || 0}</strong> quiz(zes). What would you like to do with them?
              </AlertDialogDescription>
            </AlertDialogHeader>

            <RadioGroup value={deleteMode} onValueChange={setDeleteMode} className="space-y-3 py-4">
              <div className="flex items-start space-x-3 p-3 border-2 rounded-lg border-slate-200 hover:border-slate-300 transition-colors">
                <RadioGroupItem value="move" id="move" className="mt-1" />
                <Label htmlFor="move" className="cursor-pointer flex-1">
                  <div className="font-semibold text-slate-900">Move to Uncategorized</div>
                  <div className="text-xs text-slate-600 mt-1">Safe - quizzes remain accessible without a folder</div>
                </Label>
              </div>

              <div className="flex items-start space-x-3 p-3 border-2 border-red-200 rounded-lg bg-red-50 hover:border-red-300 transition-colors">
                <RadioGroupItem value="cascade" id="cascade" className="mt-1" />
                <Label htmlFor="cascade" className="cursor-pointer flex-1">
                  <div className="font-semibold text-red-700">Delete All Quizzes Permanently</div>
                  <div className="text-xs text-red-600 mt-1">Warning: This will permanently delete all quizzes and their submissions</div>
                </Label>
              </div>
            </RadioGroup>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteFolder}
                className={deleteMode === "cascade" ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}
              >
                {deleteMode === "move" ? "Move & Delete Folder" : "Delete All Permanently"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Move Quiz Dialog */}
        <Dialog open={moveQuizDialog} onOpenChange={setMoveQuizDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="refined-header text-slate-900">Move Quiz to Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Show current folder */}
              {movingQuiz && (
                <p className="text-sm text-slate-600">
                  Currently in:{" "}
                  <span className="font-semibold">
                    {!movingQuiz.folder_id
                      ? "Uncategorized"
                      : folders.find(f => f.id === movingQuiz.folder_id)?.name || "Unknown"}
                  </span>
                </p>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Move to Folder</Label>
                <Select value={targetFolderId} onValueChange={setTargetFolderId}>
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Choose a folder" />
                  </SelectTrigger>
                  <SelectContent>
                    {movingQuiz && (() => {
                      const currentFolderId = movingQuiz.folder_id || null;
                      const availableFolders = folders.filter(f => f.id !== currentFolderId);

                      return (
                        <>
                          {/* Only show "Uncategorized" if quiz is not already uncategorized */}
                          {currentFolderId !== null && (
                            <SelectItem value="uncategorized">Uncategorized ({uncategorizedCount})</SelectItem>
                          )}
                          {/* Show other folders (excluding current) */}
                          {availableFolders.map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.name} ({f.quiz_count || 0})</SelectItem>
                          ))}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveQuizDialog(false)} className="border-slate-300">Cancel</Button>
              <Button onClick={handleMoveQuiz} className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white">
                Move Quiz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy to Campus Dialog */}
        <Dialog open={copyToCampusOpen} onOpenChange={setCopyToCampusOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="refined-header text-slate-900">Copy Quiz to Campus</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {copyToCampusQuiz && (
                <p className="text-sm text-slate-600">
                  Copying: <span className="font-semibold">{copyToCampusQuiz.title}</span>
                </p>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Target Campus</Label>
                <Select value={copyTargetCampusId} onValueChange={setCopyTargetCampusId}>
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Select a campus..." />
                  </SelectTrigger>
                  <SelectContent>
                    {campuses
                      .filter(c => c.id !== copyToCampusQuiz?.campus_id)
                      .map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {c.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">
                All sections, questions, and campus-scoped categories will be copied. The quiz opens as a draft.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyToCampusOpen(false)} className="border-slate-300">Cancel</Button>
              <Button
                onClick={confirmCopyToCampus}
                disabled={!copyTargetCampusId || copyingToCampus}
                className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {copyingToCampus ? "Copying..." : "Copy"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy to Organization Dialog (Superadmin Only) */}
        <Dialog open={copyToOrgOpen} onOpenChange={setCopyToOrgOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="refined-header text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600" />
                Copy Quiz to Another Organization
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {copyToOrgQuiz && (
                <div className="bg-slate-50 p-3 rounded-md border border-slate-200">
                  <p className="text-sm text-slate-600">
                    Copying: <span className="font-semibold text-slate-900">{copyToOrgQuiz.title}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    From: <span className="font-medium">{copyToOrgQuiz.organization?.name || selectedOrg?.name || 'Current Organization'}</span>
                  </p>
                </div>
              )}

              {/* Target Organization */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Target Organization *</Label>
                <Select
                  value={copyTargetOrgId}
                  onValueChange={(value) => {
                    setCopyTargetOrgId(value);
                    setCopyTargetOrgCampusId(""); // Reset campus when org changes
                    fetchTargetOrgCampuses(value);
                  }}
                >
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Select an organization..." />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations
                      .filter(org => org.id !== copyToOrgQuiz?.organization_id)
                      .map(org => (
                        <SelectItem key={org.id} value={org.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            {org.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  The quiz will be copied to the selected organization
                </p>
              </div>

              {/* Target Campus (Optional) */}
              {copyTargetOrgId && targetOrgCampuses.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Target Campus (Optional)</Label>
                  <Select value={copyTargetOrgCampusId} onValueChange={setCopyTargetOrgCampusId}>
                    <SelectTrigger className="h-10 border-slate-300">
                      <SelectValue placeholder="Select a campus (optional)..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No specific campus</SelectItem>
                      {targetOrgCampuses.map(campus => (
                        <SelectItem key={campus.id} value={campus.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {campus.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Custom Title (Optional) */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">New Title (Optional)</Label>
                <Input
                  value={copyNewTitle}
                  onChange={(e) => setCopyNewTitle(e.target.value)}
                  placeholder={`${copyToOrgQuiz?.title || 'Quiz'} (Copy)`}
                  className="h-10 border-slate-300"
                />
                <p className="text-xs text-slate-500">
                  Leave empty to use default: "{copyToOrgQuiz?.title} (Copy)"
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800 font-medium mb-1">What will be copied:</p>
                <ul className="text-xs text-blue-700 space-y-0.5 list-disc list-inside">
                  <li>All sections, questions, and options</li>
                  <li>Quiz settings and configurations</li>
                  <li>Categories (smart mapping)</li>
                </ul>
                <p className="text-xs text-blue-700 mt-2">
                  The quiz will be created as <span className="font-semibold">draft</span> status.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCopyToOrgOpen(false)}
                disabled={copyingToOrg}
                className="border-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmCopyToOrg}
                disabled={!copyTargetOrgId || copyingToOrg}
                className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {copyingToOrg ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Copying...
                  </>
                ) : (
                  'Copy to Organization'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Access Dialog — groups + direct users */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="refined-header text-slate-900">Manage Access</DialogTitle>
            </DialogHeader>
            {(() => {
              const q = quizzes.find((x) => x.id === assignQuizId);
              if (!q) return null;
              // sequence_only wins over status — even published, such a quiz
              // never appears in the portal on its own.
              if (q.sequence_only) {
                return (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      This quiz is marked sequence-only — it won&apos;t appear in participants&apos;
                      portals on its own. Assign the wrapping sequence instead.
                    </span>
                  </div>
                );
              }
              return q.status !== "published" ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    This quiz is {q.status === "closed" ? "closed" : "still a draft"} —
                    assigned participants won&apos;t see it in their portal until you publish it.
                  </span>
                </div>
              ) : null;
            })()}
            <div className="space-y-4">
              {/* Currently Assigned Groups */}
              {assignedGroups.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Currently Assigned Groups</Label>
                  <div className="space-y-2">
                    {assignedGroups.map((ag) => (
                      <div
                        key={ag.group_id}
                        className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-indigo-900">{ag.group_name}</p>
                          <p className="text-xs text-indigo-600">Assigned {new Date(ag.assigned_at).toLocaleDateString()}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnassignGroup(ag.group_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assign New Group */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Assign New Group</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger className="h-10 border-slate-300" data-testid="assign-group-select">
                    <SelectValue placeholder="Choose a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups
                      .filter(g => !assignedGroups.some(ag => ag.group_id === g.id))
                      .map(g => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name} ({g.member_count || 0} members)
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {groups.filter(g => !assignedGroups.some(ag => ag.group_id === g.id)).length === 0 && (
                  <p className="text-xs text-slate-500">
                    {groups.length === 0
                      ? "No groups available. Create groups first."
                      : "All available groups are already assigned."}
                  </p>
                )}
              </div>
              <Button
                onClick={handleAssignGroup}
                disabled={!selectedGroupId}
                data-testid="confirm-assign-group-btn"
                className="refined-action-button w-full bg-indigo-600 hover:bg-indigo-700 text-white h-10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Assign Group
              </Button>

              {/* Direct per-user assignment (Student Portal Phase 1) */}
              <div className="pt-4 mt-2 border-t border-slate-200">
                {assignQuizId && (
                  <AssignedUsersPicker
                    resourceType="quiz"
                    resourceId={assignQuizId}
                    onChange={() => queryClient.invalidateQueries({ queryKey: queryKeys.quizzes.all })}
                  />
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Floating Bulk Action Bar */}
      {canWriteAny && selectedQuizIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-slate-700">
          <span className="text-sm font-semibold">{selectedQuizIds.size} selected</span>
          <div className="w-px h-5 bg-slate-600" />
          <button
            onClick={selectAllVisible}
            className="text-xs text-slate-300 hover:text-white transition-colors px-2 py-1 rounded hover:bg-slate-700"
          >
            Select all {quizzes.length}
          </button>
          <button
            onClick={handleBulkPublish}
            className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Globe className="w-3.5 h-3.5" /> Publish Drafts
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300 hover:bg-red-950/50 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          <button
            onClick={clearSelection}
            className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
}
